<template>
  <v-container class="pa-0" fluid v-if="!$apollo.queries.me.loading">
    <v-layout class="px-4 py-3" row wrap>
      <v-flex xs12>
        <v-text-field
          :error-messages="firstNameErrors"
          @blur="$v.me.firstName.$touch()"
          @input="$v.me.firstName.$touch()"
          label="First Name"
          v-model="me.firstName"
        ></v-text-field>
      </v-flex>
      <v-flex xs12>
        <v-text-field
          :error-messages="lastNameErrors"
          @blur="$v.me.lastName.$touch()"
          @input="$v.me.lastName.$touch()"
          label="Last Name"
          v-model="me.lastName"
        ></v-text-field>
      </v-flex>
      <v-flex xs12>
        <v-text-field
          :error-messages="emailErrors"
          @blur="$v.me.email.$touch()"
          @input="$v.me.email.$touch()"
          label="Email"
          v-model="me.email"
        ></v-text-field>
      </v-flex>
      <v-flex v-show="me.vscId !== ''" xs12>
        <v-text-field
          @click:append="generateSecretKey"
          append-icon="refresh"
          label="Secret Key"
          readonly
          v-model="me.vscSecretKey"
        ></v-text-field>
      </v-flex>
      <v-flex v-show="me.vscId === ''" xs12>
        <v-text-field
          :error-messages="oldPasswordErrors"
          @blur="$v.password.oldPassword.$touch()"
          @input="$v.password.oldPassword.$touch()"
          label="Old Password"
          type="password"
          v-model="password.oldPassword"
        ></v-text-field>
      </v-flex>
      <v-flex v-show="me.vscId === ''" xs12>
        <v-text-field
          :error-messages="newPasswordErrors"
          @blur="$v.password.newPassword.$touch()"
          @input="$v.password.newPassword.$touch()"
          label="New Password"
          type="password"
          v-model="password.newPassword"
        ></v-text-field>
      </v-flex>
      <v-flex v-show="me.vscId === ''" xs12>
        <v-text-field
          :error-messages="repeatPasswordErrors"
          @blur="$v.password.repeatPassword.$touch()"
          @input="$v.password.repeatPassword.$touch()"
          label="Repeat Password"
          type="password"
          v-model="password.repeatPassword"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-layout class="px-3" justify-end row>
      <v-btn @click="updateMe" color="info">Save</v-btn>
    </v-layout>
  </v-container>
</template>

<script>
import { required, email, sameAs } from 'vuelidate/lib/validators';
import { ME, UPDATE_ME, UPDATE_PASSWORD } from '@/graphql.gql';
import meMixin from '@/mixins/me';

export default {
  mixins: [meMixin],
  data() {
    return {
      password: {
        oldPassword: '',
        newPassword: '',
        repeatPassword: '',
      },
    };
  },
  validations: {
    me: {
      firstName: { required },
      lastName: { required },
      email: { required, email },
    },
    password: {
      oldPassword: { required },
      newPassword: { required },
      repeatPassword: { required, sameAsNewPassword: sameAs('newPassword') },
    },
  },
  computed: {
    firstNameErrors() {
      const errors = [];
      if (!this.$v.me.firstName.$dirty) return errors;
      if (!this.$v.me.firstName.required) {
        errors.push('First name is required.');
      }
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.me.lastName.$dirty) return errors;
      if (!this.$v.me.lastName.required) {
        errors.push('Last name is required.');
      }
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.me.email.$dirty) return errors;
      if (!this.$v.me.email.email) {
        errors.push('Must be valid e-mail');
      }
      if (!this.$v.me.email.required) {
        errors.push('E-mail is required.');
      }
      return errors;
    },
    oldPasswordErrors() {
      const errors = [];
      if (this.password.oldPassword !== '' || this.password.newPassword !== '' || this.password.repeatPassword !== '') {
        if (!this.$v.password.oldPassword.$dirty) return errors;
        if (!this.$v.password.oldPassword.required) {
          errors.push('Old password is required.');
        }
      }
      return errors;
    },
    newPasswordErrors() {
      const errors = [];
      if (this.password.oldPassword !== '' || this.password.newPassword !== '' || this.password.repeatPassword !== '') {
        if (!this.$v.password.newPassword.$dirty) return errors;
        if (!this.$v.password.newPassword.required) {
          errors.push('New password is required.');
        }
      }
      return errors;
    },
    repeatPasswordErrors() {
      const errors = [];
      if (this.password.oldPassword !== '' || this.password.newPassword !== '' || this.password.repeatPassword !== '') {
        if (!this.$v.password.repeatPassword.$dirty) return errors;
        if (!this.$v.password.repeatPassword.required) {
          errors.push('Repeated password is required.');
        }
        if (!this.$v.password.repeatPassword.sameAsNewPassword) {
          errors.push('Passwords must be identical.');
        }
      }
      return errors;
    },
  },
  methods: {
    async updateMe() {
      try {
        this.$wait.start('busy');
        this.$v.$touch();
        if (this.$v.me.$invalid) {
          this.$snackbar.show('Please, fill the credentials.');
        } else {
          await this.$apollo.mutate({
            mutation: UPDATE_ME,
            variables: {
              myData: {
                firstName: this.me.firstName,
                lastName: this.me.lastName,
                email: this.me.email,
                vscSecretKey: this.me.vscSecretKey,
              },
            },
            update: (store, { data: { updateMe } }) => {
              const data = store.readQuery({ query: ME });
              data.me.firstName = updateMe.firstName;
              data.me.lastName = updateMe.lastName;
              data.me.email = updateMe.email;
              data.me.vscSecretKey = updateMe.vscSecretKey;
              store.writeQuery({ query: ME, data });
            },
            optimisticResponse: {
              __typename: 'Mutation',
              updateMe: {
                __typename: 'UpdateMe',
                firstName: this.me.firstName,
                lastName: this.me.lastName,
                email: this.me.email,
                vscSecretKey: this.me.vscSecretKey,
              },
            },
          });
        }
        if (this.me.vscId === '' && this.password.newPassword !== '') {
          if (this.$v.password.$invalid) {
            this.$snackbar.show('Please, fill the credentials.');
          } else {
            await this.$apollo.mutate({
              mutation: UPDATE_PASSWORD,
              variables: {
                input: {
                  oldPassword: this.password.oldPassword,
                  newPassword: this.password.newPassword,
                },
              },
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        this.$snackbar.show('We could not update your information. Please, try again.');
      } finally {
        this.$wait.end('busy');
      }
    },
    generateSecretKey() {
      this.me.vscSecretKey =
        Math.random()
          .toString(36)
          .substring(2, 15) +
        Math.random()
          .toString(36)
          .substring(2, 15);
    },
  },
  created() {
    this.$store.dispatch('setTitle', 'Settings');
  },
};
</script>
